/**
 * One dairy, every period — what it produced, and where that gas went.
 *
 * Two clocks meet on this table and keeping them apart is the whole design.
 * `Produced` is booked to the month as a *vintage*; `Dispensed` is whatever of
 * this dairy's gas left the bank during that *period*, of any vintage. A dairy
 * that produced in March and had it dispensed in May appears on both rows, so
 * every destination carries its own vintage and an age badge when the two
 * differ. Reading the columns as one clock is the mistake this drawer exists to
 * prevent.
 *
 * The balance column is the thread: `opening + produced − dispensed = banked`,
 * and each period's close is the next one's open. That is the inventory
 * continuity the allocation depends on, shown rather than asserted.
 */

import { Badge, Group, Paper, ScrollArea, Stack, Table, Text, Tooltip } from "@mantine/core";
import { useState } from "react";
import { useDairyHistory, useDairyRegistry } from "../../api/hooks";
import type { DairyHistoryPeriod } from "../../api/types";
import { fmtMmbtu as fmt } from "../../lib/mmbtuFormat";
import { DetailRow } from "../DetailRow";
import { DetailTable, Empty, ExpandCell, HistoryDrawer, Mmbtu, TotalStat } from "./HistoryDrawer";

export function DairyHistoryDrawer({
  dairyId,
  onClose,
  onOpenStation,
}: {
  dairyId: string | null;
  onClose: () => void;
  /** Hands the reader across to the demand side. */
  onOpenStation: (fseId: string) => void;
}) {
  const historyQ = useDairyHistory(dairyId);
  const registryQ = useDairyRegistry();
  const record = registryQ.data?.items.find((d) => d.dairy_id === dairyId) ?? null;
  const history = historyQ.data;

  return (
    <HistoryDrawer
      opened={dairyId !== null}
      onClose={onClose}
      title={history?.dairy_name ?? record?.name ?? dairyId ?? ""}
      subtitle={
        <Text size="xs" c="dimmed" ff="monospace">
          {history?.fpc_code || record?.fpc_code || "no pathway code"}
          {history ? ` · draw ${history.roster_position + 1}` : ""}
        </Text>
      }
      isPending={historyQ.isPending}
      error={historyQ.isError ? historyQ.error : null}
    >
      {history ? (
        <Stack gap="md">
          {record ? (
            <Paper p="md" radius="md" withBorder>
              <Group gap="xl" wrap="wrap">
                <DetailRow label="Workbook name" value={record.workbook_name ?? record.name} />
                <DetailRow label="Facility" value={record.facility_identifier ?? null} mono />
                <DetailRow label="Certified CI" value={record.certified_ci_gco2e_per_mj} mono />
                <DetailRow label="RIN eligible" value={record.rin_eligibility_date} mono />
              </Group>
            </Paper>
          ) : null}

          {history.periods.length === 0 ? (
            <Empty>
              This dairy has never booked production in any period the ledger covers, so nothing has
              ever been allocated from it. It sits on the roster and holds a draw position — which
              is not nothing: the position is where its volume would enter the queue once it starts
              producing.
            </Empty>
          ) : (
            <>
              <Paper p="md" radius="md" withBorder>
                <Group justify="space-between" wrap="wrap" gap="lg">
                  <TotalStat
                    label="Produced"
                    value={`${fmt(history.total_produced.value)} MMBtu`}
                    sub={`from ${history.first_production_period ?? "—"}`}
                  />
                  <TotalStat
                    label="Dispensed"
                    value={`${fmt(history.total_dispensed.value)} MMBtu`}
                    sub="attributed to stations"
                  />
                  <TotalStat
                    label="Still banked"
                    value={`${fmt(history.banked_now.value)} MMBtu`}
                    sub="awaiting a dispensing period"
                  />
                  <TotalStat
                    label="Sent to"
                    value={String(history.by_station.length)}
                    sub={history.by_station.length === 1 ? "station" : "stations"}
                  />
                </Group>
              </Paper>

              <Stack gap={4}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
                  Production &amp; sending history
                </Text>
                <Text size="xs" c="dimmed">
                  Newest first. <strong>Produced</strong> is booked to that month as a vintage;
                  <strong> dispensed</strong> is what left the bank during that period, of any
                  vintage. Open a period to see which stations took it.
                </Text>
              </Stack>

              <Paper radius="md" withBorder>
                <ScrollArea>
                  <Table highlightOnHover verticalSpacing="xs" miw={620}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Period</Table.Th>
                        <Table.Th ta="right">Opening</Table.Th>
                        <Table.Th ta="right">Produced</Table.Th>
                        <Table.Th ta="right">Dispensed</Table.Th>
                        <Table.Th ta="right">Banked</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {history.periods.map((period) => (
                        <PeriodRow
                          key={period.period}
                          period={period}
                          onOpenStation={onOpenStation}
                        />
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              </Paper>

              <Stack gap={4}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
                  Lifetime by station
                </Text>
                <Paper radius="md" withBorder>
                  <Table verticalSpacing="xs">
                    <Table.Tbody>
                      {history.by_station.map((s) => (
                        <Table.Tr key={s.fse_id}>
                          <Table.Td>
                            <Text
                              size="sm"
                              c="blue"
                              style={{ cursor: "pointer" }}
                              onClick={() => onOpenStation(s.fse_id)}
                            >
                              {s.station_name}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" ff="monospace" c="dimmed">
                              {s.fse_id}
                            </Text>
                          </Table.Td>
                          <Table.Td ta="right">
                            <Mmbtu value={s.mmbtu.value} />
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Paper>
              </Stack>
            </>
          )}
        </Stack>
      ) : null}
    </HistoryDrawer>
  );
}

/**
 * Provenance on a production figure — the same disclosure the lot inventory
 * makes, at the same grain (a lot is dairy x vintage, so exactly one production
 * row backs each period here).
 *
 * Silent for a transcribed figure, and silent again when nothing was booked: a
 * month the dairy only carried volume through has no measurement to describe,
 * and a badge claiming otherwise would invent one.
 */
function Provenance({ period }: { period: DairyHistoryPeriod }) {
  if (period.source !== "telemetry") return null;
  const substituted = period.substituted_days ?? 0;
  return (
    <Tooltip
      label={
        substituted > 0
          ? `Derived from the meters: the injected energy at the custody-transfer point, split by this dairy's own meter and analyzer. ${substituted} day${substituted === 1 ? "" : "s"} of the month came from a tier below the custody meter — the GC outlet flow, or the plant's consolidated record.`
          : "Derived from the meters: the injected energy at the custody-transfer point, split by this dairy's own meter and analyzer. Every day measured at the custody meter."
      }
      multiline
      w={320}
      withArrow
    >
      <Badge
        color={substituted > 0 ? "yellow" : "teal"}
        variant="light"
        size="xs"
        style={{ cursor: "help" }}
      >
        {substituted > 0 ? `derived · ${substituted}d` : "derived"}
      </Badge>
    </Tooltip>
  );
}

function PeriodRow({
  period,
  onOpenStation,
}: {
  period: DairyHistoryPeriod;
  onOpenStation: (fseId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const booked = period.source !== null;

  return (
    <>
      <Table.Tr>
        <Table.Td>
          <ExpandCell
            expanded={expanded}
            label={`Destinations for ${period.period}`}
            onToggle={() => setExpanded((v) => !v)}
          >
            <Text size="sm" ff="monospace">
              {period.period}
            </Text>
            <Text size="xs" c="dimmed">
              {period.destinations.length} station
              {period.destinations.length === 1 ? "" : "s"}
            </Text>
          </ExpandCell>
        </Table.Td>
        <Table.Td ta="right">
          <Mmbtu value={period.opening.value} dim />
        </Table.Td>
        <Table.Td ta="right">
          <Group gap={6} justify="flex-end" wrap="nowrap">
            {booked ? (
              <>
                <Provenance period={period} />
                <Mmbtu value={period.produced.value} />
              </>
            ) : (
              <Tooltip
                label="Nothing booked this month — the dairy appears only because it was still holding banked volume."
                withArrow
              >
                <Text size="xs" c="dimmed">
                  —
                </Text>
              </Tooltip>
            )}
          </Group>
        </Table.Td>
        <Table.Td ta="right">
          <Mmbtu value={period.dispensed.value} />
        </Table.Td>
        <Table.Td ta="right">
          <Mmbtu value={period.banked.value} dim />
        </Table.Td>
      </Table.Tr>

      {expanded ? (
        <DetailTable>
          {period.destinations.length === 0 ? (
            <Table.Tr>
              <Table.Td>
                <Text size="xs" c="dimmed" pl="lg">
                  Nothing was drawn from this dairy in this period — its volume stayed banked.
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            period.destinations.map((row, idx) => (
              <Table.Tr key={`${row.fse_id}:${row.vintage_month}:${idx}`}>
                <Table.Td pl="xl">
                  <Text
                    size="sm"
                    c="blue"
                    style={{ cursor: "pointer" }}
                    onClick={() => onOpenStation(row.fse_id)}
                  >
                    {row.station_name}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm" ff="monospace">
                      {row.vintage_month}
                    </Text>
                    {row.vintage_month !== period.period ? (
                      <Tooltip
                        label={`Produced ${row.age_periods} period${row.age_periods === 1 ? "" : "s"} earlier and banked until this one`}
                        withArrow
                      >
                        <Badge color="grape" variant="light" size="xs">
                          +{row.age_periods}
                        </Badge>
                      </Tooltip>
                    ) : null}
                  </Group>
                </Table.Td>
                <Table.Td ta="right">
                  <Mmbtu value={row.mmbtu.value} />
                </Table.Td>
              </Table.Tr>
            ))
          )}
        </DetailTable>
      ) : null}
    </>
  );
}
